'use client'

import * as React from 'react'
import {
    ClientOnly,
    IconButton,
    type IconButtonProps,
    Skeleton,
    Span,
    type SpanProps,
} from '@chakra-ui/react'
import { ThemeProvider, type ThemeProviderProps, useTheme } from 'next-themes'
import { LuMoon, LuSun } from 'react-icons/lu'

export interface ColorModeProviderProps extends ThemeProviderProps {}

export function ColorModeProvider(props: ColorModeProviderProps) {
    return <ThemeProvider disableTransitionOnChange attribute='class' {...props} />
}

export type ColorMode = 'light' | 'dark'

export interface UseColorModeReturn {
    colorMode: ColorMode
    setColorMode: (colorMode: ColorMode) => void
    toggleColorMode: () => void
}

export function useColorMode(): UseColorModeReturn {
    const { resolvedTheme, setTheme, forcedTheme } = useTheme()
    const colorMode = forcedTheme || resolvedTheme
    const toggleColorMode = () => {
        setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
    }

    return {
        colorMode: colorMode as ColorMode,
        setColorMode: setTheme,
        toggleColorMode,
    }
}

export function useColorModeValue<T>(light: T, dark: T) {
    const { colorMode } = useColorMode()

    return colorMode === 'dark' ? dark : light
}

export function ColorModeIcon() {
    const { colorMode } = useColorMode()

    return colorMode === 'dark' ? <LuMoon /> : <LuSun />
}

interface ColorModeButtonProps extends Omit<IconButtonProps, 'aria-label'> {}

export const ColorModeButton = React.forwardRef<HTMLButtonElement, ColorModeButtonProps>(
    function ColorModeButton(props, ref) {
        const { toggleColorMode } = useColorMode()

        return (
            <ClientOnly fallback={<Skeleton boxSize='9' />}>
                <IconButton
                    ref={ref}
                    aria-label='Toggle color mode'
                    size='sm'
                    variant='ghost'
                    onClick={toggleColorMode}
                    {...props}
                    css={{
                        _icon: {
                            width: '5',
                            height: '5',
                        },
                    }}
                >
                    <ColorModeIcon />
                </IconButton>
            </ClientOnly>
        )
    }
)

export const LightMode = React.forwardRef<HTMLSpanElement, SpanProps>(
    function LightMode(props, ref) {
        return (
            <Span
                ref={ref}
                backgroundColor='custard.100'
                className='chakra-theme light'
                color='fg'
                colorPalette='gray'
                colorScheme='light'
                display='contents'
                {...props}
            />
        )
    }
)

export const DarkMode = React.forwardRef<HTMLSpanElement, SpanProps>(function DarkMode(props, ref) {
    return (
        <Span
            ref={ref}
            className='chakra-theme dark'
            color='fg'
            colorPalette='gray'
            colorScheme='dark'
            display='contents'
            {...props}
        />
    )
})
